import { useInfiniteQuery } from "react-query";
import { supabase } from "../../../config/supabaseClient";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { StaticVariables } from "../../StaticVariables";
import { reactQueryParams } from "../../StayUpToDate/RenderComponent";
import { useCallback, useMemo, useRef } from "react";
import { tagTrendingProductsAsProductType } from "../../productImage/productImageDisplay";
import {
  getOrComputeTrendingPostIds,
  type TrendingClickSource,
  type TrendingSnapshotPersist,
} from "./trendingDailySnapshot";
import { resolveTrendingAnalyticsDates } from "../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorTrendingDetails";

dayjs.extend(utc);
dayjs.extend(timezone);
const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
/** First paint + scroll pages — 100 forced a huge RPC/payload before any tiles. */
const POSTS_PER_PAGE = 24;
/**
 * Same RPC window as `useLoadTrendingProductsMaxCount` (LiB strip / "N more items").
 * Individual / View All must not page past this set or the catalog outgrows the tile.
 */
const TRENDING_PRODUCTS_RPC_WINDOW = 200;

/** Recent Products / Trending Products: LTK, ShopMy, Amazon, MISC only — not IG/TikTok shoppable content. */
function isProductPlatformOnly(platform: unknown): boolean {
  const value = String(platform || "")
    .trim()
    .toLowerCase();
  if (!value) return false;
  const allowed = StaticVariables().PRODUCT_PLATFORMS.map((p: string) =>
    String(p).toLowerCase()
  );
  return allowed.includes(value) || value === "misc-product";
}

function isValidShopLink(shopLink: unknown): boolean {
  return typeof shopLink === "string" && shopLink.trim() !== "";
}

export function useLoadTrendingProducts(
  user_id: string,
  username: any,
  title: any,
  numberOfClicks: any,
  removedPostIds: any,
  startDate: any,
  endDate: any,
  days: any,
  order: string = "DESC",
  type?: string,
  enabled: boolean = true,
  customOrderPostIds: string[] = [],
  snapshotPersist?: TrendingSnapshotPersist,
  /** Edit modal can force a fresh analytics pull when intentionally refreshing. */
  bypassDailySnapshot: boolean = false,
  /**
   * Website Trending Products passes `template-shop`. Website Trending Content
   * passes `template-content`. Default stays the link-in-bio analytics RPC.
   */
  clickSource: TrendingClickSource = "legacy",
) {
  const getComparableTimestamp = (post: any) => {
    const candidates = [
      post?.post_date,
      post?.postDate,
      post?.published_at,
      post?.created_at,
      post?.createdAt,
      post?.timestamp,
      post?.post_time,
    ];
    for (const value of candidates) {
      if (!value) continue;
      const time = new Date(value).getTime();
      if (!Number.isNaN(time)) {
        return time;
      }
    }
    return 0;
  };

  const applyOrdering = (items: any[]) => {
    if (!Array.isArray(items)) return [];

    if (
      order === "CUSTOM" &&
      Array.isArray(customOrderPostIds) &&
      customOrderPostIds.length > 0
    ) {
      const idToItem = new Map<string, any>();
      const baseSorted = [...items].sort(
        (a, b) => getComparableTimestamp(b) - getComparableTimestamp(a)
      );

      baseSorted.forEach((item) => {
        idToItem.set(String(item?.post_id), item);
      });

      const ordered = customOrderPostIds
        .map((id) => idToItem.get(String(id)))
        .filter(Boolean);

      const usedIdSet = new Set(customOrderPostIds.map((id) => String(id)));
      const remaining = baseSorted.filter(
        (item) => !usedIdSet.has(String(item?.post_id))
      );

      return [...ordered, ...remaining];
    }

    if (order === "ASC") {
      return [...items].sort(
        (a, b) => getComparableTimestamp(a) - getComparableTimestamp(b)
      );
    }

    // Default to DESC (newest first)
    return [...items].sort(
      (a, b) => getComparableTimestamp(b) - getComparableTimestamp(a)
    );
  };

  // Memoize processed removedPostIds
  const processedRemovedPostIds = useMemo(() => {
    if (!removedPostIds || removedPostIds === "") return [];
    return Array.from(
      new Set(removedPostIds.split(",").filter((elem: any) => elem !== ""))
    );
  }, [removedPostIds]);

  // Memoize date calculation
  const calculatedDates = useMemo(() => {
    /**
     * Preset ranges pass YYYY-MM-DD strings from getCustomDateRange.
     * Custom range uses dayjs instances from CustomDateRange — regex-only checks fail on those,
     * so we normalize to calendar dates in the user's timezone first.
     */
    const toYYYYMMDD = (value: any): string => {
      if (value == null || value === "") return "";
      if (typeof value === "string") {
        const t = value.trim();
        return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
      }
      const d = dayjs(value);
      return d.isValid() ? d.tz(userTimezone).format("YYYY-MM-DD") : "";
    };

    const normalizedStart = toYYYYMMDD(startDate);
    const normalizedEnd = toYYYYMMDD(endDate);

    // Preset / All tabs store absolute dates that go stale — always roll to
    // "now". Custom keeps the stored range.
    const resolved = resolveTrendingAnalyticsDates(
      `${numberOfClicks || 1},${normalizedStart},${normalizedEnd},${days ?? 7}`,
    );

    return {
      calcStartDate: resolved.startDate,
      calcEndDate: resolved.endDate,
    };
  }, [startDate, endDate, days, numberOfClicks]);

  // Cache for trending post IDs (shared across pages within this hook instance)
  const trendingPostIdsCacheRef = useRef<{
    key: string;
    postIds: string[];
  } | null>(null);
  /** Filtered product rows for the LiB strip window — client-paged for View All. */
  const trendingProductsWindowRef = useRef<{
    key: string;
    products: any[];
    postIds: string[];
  } | null>(null);

  // Fetch trending post IDs (daily snapshot → analytics once/day)
  const fetchTrendingPostIds = useCallback(async (): Promise<string[]> => {
    const { calcStartDate, calcEndDate } = calculatedDates;
    const isNotDateFormat = (dateString: any) => {
      const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
      return !dateFormatRegex.test(dateString);
    };

    if (
      !calcStartDate ||
      !calcEndDate ||
      isNotDateFormat(calcStartDate) ||
      isNotDateFormat(calcEndDate)
    ) {
      return [];
    }

    const cacheKey = `${calcStartDate}-${calcEndDate}-${numberOfClicks}-${clickSource}-${bypassDailySnapshot ? "fresh" : "snap"}`;
    if (trendingPostIdsCacheRef.current?.key === cacheKey) {
      return trendingPostIdsCacheRef.current.postIds;
    }

    const { postIds } = await getOrComputeTrendingPostIds({
      params: {
        userId: user_id,
        username,
        numberOfClicks,
        startDate: calcStartDate,
        endDate: calcEndDate,
        type,
        clickSource,
      },
      persist: snapshotPersist,
      bypassSnapshot: bypassDailySnapshot,
      label: `TrendingPaginated:${type || "products"}`,
    });

    trendingPostIdsCacheRef.current = { key: cacheKey, postIds };
    return postIds;
  }, [
    calculatedDates,
    username,
    user_id,
    numberOfClicks,
    type,
    snapshotPersist,
    bypassDailySnapshot,
    clickSource,
  ]);

  // Main paginated fetch function
  const fetchTrendingProducts = useCallback(
    async ({ pageParam = 1 } = {}) => {
      const { calcStartDate, calcEndDate } = calculatedDates;
      const isNotDateFormat = (dateString: any) => {
        const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
        return !dateFormatRegex.test(dateString);
      };

      if (
        !calcStartDate ||
        !calcEndDate ||
        isNotDateFormat(calcStartDate) ||
        isNotDateFormat(calcEndDate)
      ) {
        return {
          data: [],
          total_count: 0,
          originalDataLength: 0,
          post_ids: [],
        };
      }

      // Get trending post IDs (cached / daily snapshot)
      const trendingPostIds = await fetchTrendingPostIds();

      if (!trendingPostIds || trendingPostIds.length === 0) {
        return {
          data: [],
          total_count: 0,
          originalDataLength: 0,
          post_ids: [],
        };
      }

      // Include removed post IDs in the request so we can show the "Removed" section
      const postIdsForRpc = Array.from(
        new Set([...trendingPostIds, ...processedRemovedPostIds])
      );

      // Calculate offset for pagination
      const offset = (pageParam - 1) * POSTS_PER_PAGE;

      // Ensure type check is case-insensitive and strict
      const contentType = type?.toLowerCase()?.trim();

      // Trending products: one RPC window matching the LiB strip MaxCount loader,
      // then filter to product platforms and page that list client-side. Server
      // offset over mixed content+product IDs either stopped at ~2 or grew past
      // the strip's "N more items" total.
      if (contentType !== "trending content") {
        const windowKey = [
          calcStartDate,
          calcEndDate,
          numberOfClicks,
          order,
          Array.isArray(customOrderPostIds)
            ? customOrderPostIds.join(",")
            : "",
          postIdsForRpc.join(","),
          clickSource,
          bypassDailySnapshot ? "fresh" : "snap",
        ].join("|");

        let allProducts = trendingProductsWindowRef.current?.products;
        let allPostIds = trendingProductsWindowRef.current?.postIds;
        if (
          !allProducts ||
          trendingProductsWindowRef.current?.key !== windowKey
        ) {
          // Same RPC + args as useLoadTrendingProductsMaxCount so the catalog
          // product set matches the strip's total_count / "N more items".
          const { data: searchResult, error } = await supabase.rpc(
            "searchresulltte_with_countor",
            {
              userid: user_id,
              username: username,
              postids: postIdsForRpc,
              max_count: TRENDING_PRODUCTS_RPC_WINDOW,
              post_order: order,
              custom_order: customOrderPostIds || [],
            },
          );
          if (error) throw error;
          const result = Array.isArray(searchResult?.posts)
            ? searchResult.posts
            : [];
          const filteredArray = Array.from(
            new Map(
              result
                .filter(
                  (obj: any) =>
                    isValidShopLink(obj?.shop_link) &&
                    isProductPlatformOnly(obj?.platform),
                )
                .map((post: any) => [post.post_id, post]),
            ).values(),
          );
          allProducts = tagTrendingProductsAsProductType(
            applyOrdering(filteredArray),
            type,
          );
          allPostIds = searchResult?.post_ids || trendingPostIds;
          trendingProductsWindowRef.current = {
            key: windowKey,
            products: allProducts,
            postIds: allPostIds,
          };
        }

        const page = allProducts.slice(offset, offset + POSTS_PER_PAGE);
        return {
          data: page,
          // Product count in the strip window — matches See More (`total - 25`).
          total_count: allProducts.length,
          originalDataLength: page.length,
          post_ids: allPostIds || trendingPostIds,
        };
      }

      let searchResult: any;
      const { data, error } = await supabase.rpc(
        "search_postids_content_with_countordd",
        {
          userid: user_id,
          username: username,
          postids: postIdsForRpc,
          socials: StaticVariables().CONTENT_PLATFORMS,

          post_order: order,
          custom_order: customOrderPostIds || [],
          limit_num: POSTS_PER_PAGE,
          offset_num: offset,
        },
      );
      if (error) throw error;
      searchResult = data;

      const result = Array.isArray(searchResult?.posts)
        ? searchResult.posts
        : [];
      const total_count = searchResult?.total_count || 0;
      const allPostIds = searchResult?.post_ids || trendingPostIds;

      const orderedArray = applyOrdering(result);
      const taggedArray = tagTrendingProductsAsProductType(orderedArray, type);

      return {
        data: taggedArray,
        total_count: total_count,
        originalDataLength: result.length,
        post_ids: allPostIds,
      };
    },
    [
      calculatedDates,
      fetchTrendingPostIds,
      type,
      user_id,
      username,
      order,
      customOrderPostIds,
      processedRemovedPostIds,
      applyOrdering,
      numberOfClicks,
      bypassDailySnapshot,
    ]
  );
  // Serialize removedPostIds for query key to ensure React Query detects changes
  const removedPostIdsKey = useMemo(() => {
    if (!removedPostIds || removedPostIds === "") {
      return "";
    }
    return removedPostIds
      .split(",")
      .map((id: any) => String(id).trim())
      .filter((id: string) => id !== "")
      .sort() // Sort to ensure consistent key regardless of order
      .join(",");
  }, [removedPostIds]);

  const queryKey = useMemo(
    () => [
      "TrendingProducts",
      user_id,
      numberOfClicks,
      removedPostIdsKey, // Use serialized string for reliable change detection
      startDate,
      endDate,
      days,
      type,
      order === "CUSTOM" ? "DESC" : order,
      snapshotPersist?.componentId || "",
      bypassDailySnapshot,
      clickSource,
    ],
    [
      user_id,
      numberOfClicks,
      removedPostIdsKey,
      startDate,
      endDate,
      days,
      type,
      order === "CUSTOM" ? "DESC" : order,
      snapshotPersist?.componentId,
      bypassDailySnapshot,
      clickSource,
    ]
  );

  const queryResult = useInfiniteQuery(queryKey, fetchTrendingProducts, {
    enabled:
      enabled &&
      Boolean(title) &&
      Number(numberOfClicks) >= 1 &&
      Boolean(
        Array.from(
          new Set(
            removedPostIds?.split(",").filter((elem: any) => elem.trim() !== "")
          )
        ).length >= 0
      ),
    getNextPageParam: (lastPage: any, allPages: any) => {
      // Check if there are more pages based on original data length
      if (!lastPage || !lastPage.data) {
        return undefined;
      }

      // If original data length is less than POSTS_PER_PAGE, we've reached the end
      if (lastPage.originalDataLength !== undefined) {
        if (lastPage.originalDataLength < POSTS_PER_PAGE) {
          return undefined;
        }
      } else {
        // Fallback: check filtered data length
        if (lastPage.data.length < POSTS_PER_PAGE) {
          return undefined;
        }
      }

      // Check total loaded items against total_count
      const totalLoaded = allPages.reduce((sum: number, page: any) => {
        const pageDataLength =
          page?.originalDataLength !== undefined
            ? page.originalDataLength
            : page?.data?.length || 0;
        return sum + pageDataLength;
      }, 0);

      // If we've loaded all items based on total_count, stop
      if (lastPage.total_count && totalLoaded >= lastPage.total_count) {
        return undefined;
      }

      // Otherwise, return the next page number
      return allPages.length + 1;
    },
    keepPreviousData: true,
    ...reactQueryParams(),
    staleTime: 24 * 60 * 60 * 1000,
    cacheTime: 24 * 60 * 60 * 1000,
  });

  // Transform the result to maintain backward compatibility
  const firstPage = queryResult.data?.pages?.[0];
  const totalCount =
    firstPage && typeof firstPage === "object" && "total_count" in firstPage
      ? firstPage.total_count
      : 0;

  // Get post_ids from first page (RPC returns all post_ids in first page response)
  const postIds =
    firstPage && typeof firstPage === "object" && "post_ids" in firstPage
      ? firstPage.post_ids || []
      : [];

  // Flatten pages into single array for backward compatibility
  const flattenedPages =
    queryResult.data?.pages?.flatMap((page: any) => page.data || []) || [];
  const flattenedData =
    order === "CUSTOM" ? applyOrdering(flattenedPages) : flattenedPages;

  return {
    ...queryResult,
    data: flattenedData, // Flattened array for backward compatibility
    length: flattenedData.length, // Length of the data array
    post_ids: postIds, // All post IDs from RPC response
    dataPages: queryResult.data?.pages || [], // Keep pages structure
    total_count: postIds.length,
    // Do not OR isFetching: refetches would keep loaders stuck on skeleton
    // (same pattern as useLoadYesterdayLinks).
    isLoading: queryResult.isLoading,
    refetch: queryResult.refetch,
    fetchNextPage: queryResult.fetchNextPage,
    hasNextPage: queryResult.hasNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
  };
}
